import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User, Permission, UserRole } from '../types';
import {
  SUCCEED_PRODUCT_COURSE_PLANNER_NEXT,
  SucceedUserLevel,
} from '../types/succeed-user-session';
import {
  isEmbeddedInSucceed,
  getSucceedPermissions,
  getDistrictId,
  isQaSucceedApiProxyDev,
} from '../utils/cp-context';
import { setSucceedCoursePlannerProductLevelFromSession } from '../utils/succeed-course-planner-product-level-cache';
import {
  fetchSucceedUserSession,
  getSucceedUserSessionErrorToast,
  isSucceedUserSessionRequestAborted,
  resetSucceedUserSessionCache,
} from '../clients/fetch-succeed-user-session';
import { useToastNotifications } from '../hooks/use-toast-notifications';
import {
  clearCourseCatalogFilterSession,
  clearFiltersIfUserChanged,
} from '../constants/course-catalog-constants';

/** Reactive subscription scope resolved from the staff Succeed session. */
export interface SucceedSubscriptionScope {
  status: 'loading' | 'resolved' | 'error';
  productLevel?: string;
  highschoolId?: string;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  succeedSubscriptionScope: SucceedSubscriptionScope;
  /** Raw Succeed permission IDs when embedded. Use when needed. */
  succeedPermissions?: Record<string, number>;
  /** Check Succeed permission by ID when embedded (e.g. hasSucceedPermission(440)). */
  hasSucceedPermission: (permissionId: string | number) => boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  getToken: () => string | null;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasRole: (role: UserRole) => boolean;
}

const defaultContext: AuthContextValue = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  succeedSubscriptionScope: { status: 'loading' },
  hasSucceedPermission: () => false,
  login: () => {},
  logout: () => {},
  getToken: () => null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  hasRole: () => false,
};

export const AuthContext = createContext<AuthContextValue>(defaultContext);

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = 'auth_token';

/**
 * Reads the SUCCEED_PROFILE cookie value.
 * This cookie is set by Succeed's PHP backend and changes on every login,
 * so it uniquely identifies who is currently logged in.
 */
function getSucceedProfileFromCookie(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(/(?:^|;\s*)SUCCEED_PROFILE=([^;]+)/);
  return match ? match[1] : null;
}

function bootstrapUserFromCPContext(): User | null {
  if (typeof window === 'undefined' || !window.CP_CONTEXT) {
    return null;
  }
  const ctx = window.CP_CONTEXT;
  const isSuperUser = ctx.SUPER_USER === '1' || ctx.SUPER_USER === 'true';
  return {
    id: 'succeed-embedded',
    email: '',
    firstName: '',
    lastName: '',
    role: isSuperUser ? 'admin' : 'staff',
    permissions: [], // Raw USER_PERMISSIONS captured in succeedPermissions; use when needed
    districtId: getDistrictId(),
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { showToast } = useToastNotifications();

  const [user, setUser] = useState<User | null>(() => {
    if (!isEmbeddedInSucceed()) {
      return null;
    }
    const bootstrapped = bootstrapUserFromCPContext();
    // Succeed logout is a server-side redirect with no JS event.
    // On the next CP mount the SUCCEED_PROFILE cookie has a new value —
    // compare it against what we stored to detect a user switch and clear stale filters.
    const profileId = getSucceedProfileFromCookie();
    if (profileId) {
      // Use a composite key: profileId + school context.
      // A district user switching to a school changes HIGHSCHOOL_ID while keeping the same
      // SUCCEED_PROFILE, so a user-only key would not detect the context change.
      const highschoolId =
        (typeof window !== 'undefined' && window.CP_CONTEXT?.HIGHSCHOOL_ID) || '';
      clearFiltersIfUserChanged(`${profileId}:${highschoolId}`);
    }
    return bootstrapped;
  });
  const [succeedPermissions, setSucceedPermissions] = useState<Record<string, number> | undefined>(
    () => (isEmbeddedInSucceed() ? getSucceedPermissions() : undefined)
  );

  const [isLoading, setIsLoading] = useState(() => !isEmbeddedInSucceed());
  const [succeedSubscriptionScope, setSucceedSubscriptionScope] =
    useState<SucceedSubscriptionScope>(() => ({
      status: isEmbeddedInSucceed() || isQaSucceedApiProxyDev() ? 'loading' : 'resolved',
    }));

  /** c-p-ui parity: succeed-api GET /user?product=7 on staff embed (and QA succeed-api dev proxy). */
  useEffect(() => {
    if (!isEmbeddedInSucceed() && !isQaSucceedApiProxyDev()) {
      return;
    }
    if (isEmbeddedInSucceed() && !user) {
      return;
    }
    let cancelled = false;
    setSucceedSubscriptionScope({ status: 'loading' });
    void (async () => {
      try {
        const session = await fetchSucceedUserSession();
        if (cancelled) {
          return;
        }
        setSucceedCoursePlannerProductLevelFromSession(session);
        if (!session) {
          setSucceedSubscriptionScope({ status: 'error' });
          return;
        }
        const productLevel = session.products?.[String(SUCCEED_PRODUCT_COURSE_PLANNER_NEXT)];
        const hasSubscriptionScope =
          productLevel === SucceedUserLevel.DISTRICT ||
          productLevel === SucceedUserLevel.SCHOOL_DISTRICT ||
          productLevel === SucceedUserLevel.SCHOOL_INDIVIDUAL;
        setSucceedSubscriptionScope({
          status: hasSubscriptionScope ? 'resolved' : 'error',
          productLevel: productLevel ?? undefined,
          highschoolId: session.highschoolId,
        });
        setUser((prev) => {
          if (!prev) {
            return prev;
          }
          const email =
            session.email !== undefined && session.email !== '' ? session.email : prev.email;
          const districtId =
            session.districtId !== undefined && session.districtId !== ''
              ? session.districtId
              : prev.districtId;
          // Effect depends on `user`; returning a new object when nothing changed retriggers fetch.
          if (email === prev.email && districtId === prev.districtId) {
            return prev;
          }
          return { ...prev, email, districtId };
        });
        if (session.permissions?.length) {
          setSucceedPermissions((prev) => {
            const base = prev ?? {};
            const next = { ...base };
            for (const p of session.permissions!) {
              next[String(p)] = 1;
            }
            return next;
          });
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }
        setSucceedCoursePlannerProductLevelFromSession(null);
        setSucceedSubscriptionScope({ status: 'error' });
        if (isSucceedUserSessionRequestAborted(error)) {
          return;
        }
        const { heading, subheading } = getSucceedUserSessionErrorToast(error);
        showToast('error', { heading, subheading });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast, user]);

  const getToken = useCallback(() => {
    if (isEmbeddedInSucceed()) {
      return null;
    }
    return sessionStorage.getItem(TOKEN_KEY);
  }, []);

  const login = useCallback((newUser: User, token: string) => {
    sessionStorage.setItem(TOKEN_KEY, token);
    clearFiltersIfUserChanged(newUser.id);
    setUser(newUser);
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    clearCourseCatalogFilterSession();
    resetSucceedUserSessionCache();
    setSucceedCoursePlannerProductLevelFromSession(null);
    setSucceedSubscriptionScope({ status: 'error' });
    setUser(null);
    setIsLoading(false);
  }, []);

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) {
        return false;
      }
      // Admin has all permissions
      if (user.role === 'admin') {
        return true;
      }
      return user.permissions.includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasRole = useCallback(
    (role: UserRole): boolean => {
      if (!user) {
        return false;
      }
      return user.role === role;
    },
    [user]
  );

  /** Merges CP_CONTEXT permissions with GET /user?product=7 response (see succeedPermissions state). */
  const hasSucceedPermissionForUser = useCallback(
    (permissionId: string | number): boolean => {
      const perms = succeedPermissions;
      if (!perms) {
        return false;
      }
      const key = String(permissionId);
      return key in perms && !!perms[key];
    },
    [succeedPermissions]
  );

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    succeedSubscriptionScope,
    succeedPermissions,
    hasSucceedPermission: hasSucceedPermissionForUser,
    login,
    logout,
    getToken,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to check a single permission
 */
export function usePermission(permission: Permission): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * Hook to check user role
 */
export function useUserRole(): UserRole | null {
  const { user } = useAuth();
  return user?.role ?? null;
}
export function useDistrictId(): string {
  const { user } = useAuth();
  return user?.districtId ?? '';
}
